import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import endOfMonth from 'date-fns/endOfMonth';
import endOfWeek from 'date-fns/endOfWeek';
import format from 'date-fns/format';
import getDay from 'date-fns/getDay';
import isSameMonth from 'date-fns/isSameMonth';
import isWithinInterval from 'date-fns/isWithinInterval';
import ru from 'date-fns/locale/ru';
import startOfMonth from 'date-fns/startOfMonth';
import startOfWeek from 'date-fns/startOfWeek';

import CalendarIcon from '@/assets/icons/calendar-filled.svg?react';
import FilterIcon from '@/assets/icons/filter.svg?react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { Receipt } from '../../model';
import { formatDateRu, formatMedicinePlural } from '../../utils';
import { MonthPickerModal } from '../molecules/MonthPickerModal';
import { MedicineFilterModal } from './MedicineFilterModal';

type Props = {
  anchorDate: Date;
  onDateChange: (date: Date) => void;
  receipts: Receipt[];
  selectedWeekIndex: number;
  onSelectWeek: (index: number) => void;
};

const palette = [
  'bg-[rgba(44,154,238,0.24)]', // Синий
  'bg-[rgba(249,62,62,0.2)]', // Красный
  'bg-white', // Белый
  'bg-[rgba(95,217,142,0.5)]', // Зеленый
];

const paletteBorder = [
  'border-[#2c9aee]', // Для синей плашки - темный синий
  'border-[#f93e3e]', // Для красной плашки - темный красный
  'border-[#5fd98e]', // Для белой плашки - зеленый
  'border-[#5fd98e]', // Для зеленой плашки - темный зеленый
];

const paletteNoteBg = [
  'bg-[rgba(44,154,238,0.15)]', // Для синей плашки - более тусклый синий
  'bg-[rgba(249,62,62,0.12)]', // Для красной плашки - более тусклый красный
  'bg-[#f7f8f9]', // Для белой плашки - серый (как было)
  'bg-[rgba(95,217,142,0.35)]', // Для зеленой плашки - более тусклый зеленый
];

const dayNames = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

export const ClientCalendar = ({
  anchorDate,
  onDateChange,
  receipts,
  selectedWeekIndex,
  onSelectWeek,
}: Props) => {
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedMedicineKeys, setSelectedMedicineKeys] = useState<string[]>(
    [],
  );
  const calendarButtonRef = useRef<HTMLButtonElement>(null);
  const hasInitializedCurrentWeek = useRef(false);

  const getMedicineKey = (m: { id?: number | null; name: string }) => {
    if (m.id !== null && m.id !== undefined) return `id:${m.id}`;
    return `name:${m.name}`;
  };

  const viewStart = useMemo(
    () => startOfWeek(startOfMonth(anchorDate), { weekStartsOn: 1 }),
    [anchorDate],
  );
  const viewEnd = useMemo(
    () => endOfWeek(endOfMonth(anchorDate), { weekStartsOn: 1 }),
    [anchorDate],
  );

  const monthTitle = useMemo(
    () => formatDateRu(anchorDate, 'LLLL yyyy'),
    [anchorDate],
  );

  const weekMeta = useMemo(() => {
    if (!viewStart || !viewEnd) {
      return {
        weekRanges: [] as Array<{
          range: string;
          subtitle: string;
          start: Date;
          end: Date;
        }>,
        weekStartDates: [] as Date[],
      };
    }
    const weeks: Array<{ start: Date; end: Date }> = [];
    let cur = startOfWeek(viewStart, { weekStartsOn: 1 });
    while (cur <= viewEnd) {
      const wStart = cur;
      const wEnd = endOfWeek(cur, { weekStartsOn: 1 });
      weeks.push({ start: wStart, end: wEnd });
      cur = addDays(cur, 7);
    }
    const ranges = weeks.map(({ start, end }) => {
      const from = format(start, 'dd', { locale: ru });
      const to = format(end, 'dd', { locale: ru });
      const sameMonth = isSameMonth(start, end);
      const sub = sameMonth
        ? format(end, 'LLLL', { locale: ru })
        : `${format(start, 'LLL', { locale: ru })} ${format(start, 'yyyy', {
            locale: ru,
          })} - ${format(end, 'LLL', { locale: ru })} ${format(end, 'yyyy', {
            locale: ru,
          })}`;
      return { range: `${from} – ${to}`, subtitle: sub, start, end };
    });
    return { weekRanges: ranges, weekStartDates: weeks.map((w) => w.start) };
  }, [viewStart, viewEnd]);

  // Автоматически выбираем текущую неделю при открытии календаря
  useEffect(() => {
    if (
      !hasInitializedCurrentWeek.current &&
      weekMeta.weekStartDates.length > 0
    ) {
      const today = new Date();
      const currentWeekIndex = weekMeta.weekStartDates.findIndex(
        (weekStart) => {
          const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
          return isWithinInterval(today, { start: weekStart, end: weekEnd });
        },
      );
      if (currentWeekIndex !== -1) {
        onSelectWeek(currentWeekIndex);
        hasInitializedCurrentWeek.current = true;
      }
    }
  }, [weekMeta.weekStartDates, onSelectWeek]);

  const weekSchedules = useMemo(() => {
    if (!viewStart || !viewEnd) {
      return [] as Array<
        Array<{
          day: string;
          date: string;
          dateObj: Date;
          medications: Array<{
            name: string;
            dosage: string;
            time: string;
            bgColor: string;
            note?: string;
            noteBorderColor?: string;
            noteBgColor?: string;
          }>;
        }>
      >;
    }
    const byDate: Record<
      string,
      Array<{
        name: string;
        dosage: string;
        time: string;
        bgColor: string;
        note?: string;
        noteBorderColor?: string;
        noteBgColor?: string;
        originalColorIndex: number;
      }>
    > = {};
    for (const r of receipts) {
      for (const rm of r.receipt_medicine) {
        if (selectedMedicineKeys.length > 0) {
          const key = getMedicineKey(rm.medicine);
          if (!selectedMedicineKeys.includes(key)) continue;
        }
        for (let i = 0; i < rm.duration; i++) {
          const d = addDays(r.start_date, i);
          if (!isWithinInterval(d, { start: viewStart, end: viewEnd })) {
            continue;
          }
          const key = format(d, 'yyyy-MM-dd');
          // Если нет id, используем рандомный цвет на основе имени лекарства
          const medicineId = rm.medicine.id ?? 0;
          const hash =
            medicineId ||
            rm.medicine.name
              .split('')
              .reduce((acc, char) => acc + char.charCodeAt(0), 0);
          const colorIndex = hash % palette.length;
          const dosage = `${rm.amount} ${formatMedicinePlural(
            Number(rm.amount),
          )} в день`;
          const time = rm.daytime.value;
          const note = rm.eating?.value
            ? `Принимать ${rm.eating.value.toLowerCase()}`
            : undefined;
          if (!byDate[key]) {
            byDate[key] = [];
          }
          byDate[key].push({
            name: rm.medicine.name,
            dosage,
            time,
            bgColor: palette[colorIndex],
            note,
            noteBorderColor: paletteBorder[colorIndex],
            noteBgColor: paletteNoteBg[colorIndex],
            originalColorIndex: colorIndex,
          });
        }
      }
    }

    // Перераспределяем цвета так, чтобы в одном дне не было одинаковых подряд
    for (const key in byDate) {
      const medications = byDate[key];
      if (medications.length <= 1) continue;

      // Создаем массив с перераспределенными цветами
      const redistributed: Array<(typeof medications)[0]> = [];
      let lastColorIndex = -1;

      for (let i = 0; i < medications.length; i++) {
        const med = medications[i];
        let colorIndex = med.originalColorIndex;

        // Если текущий цвет совпадает с предыдущим, выбираем другой
        if (colorIndex === lastColorIndex) {
          // Пробуем все цвета по кругу, начиная со следующего
          for (let offset = 1; offset < palette.length; offset++) {
            const nextIndex = (colorIndex + offset) % palette.length;
            if (nextIndex !== lastColorIndex) {
              colorIndex = nextIndex;
              break;
            }
          }
        }

        lastColorIndex = colorIndex;
        redistributed.push({
          name: med.name,
          dosage: med.dosage,
          time: med.time,
          bgColor: palette[colorIndex],
          note: med.note,
          noteBorderColor: paletteBorder[colorIndex],
          noteBgColor: paletteNoteBg[colorIndex],
        });
      }

      byDate[key] = redistributed;
    }
    const weeks: Array<
      Array<{
        day: string;
        date: string;
        dateObj: Date;
        medications: Array<{
          name: string;
          dosage: string;
          time: string;
          bgColor: string;
          note?: string;
          noteBorderColor?: string;
        }>;
      }>
    > = [];
    let cur = startOfWeek(viewStart, { weekStartsOn: 1 });
    while (cur <= viewEnd) {
      const days: Array<{
        day: string;
        date: string;
        dateObj: Date;
        medications: Array<{
          name: string;
          dosage: string;
          time: string;
          bgColor: string;
          note?: string;
          noteBorderColor?: string;
        }>;
      }> = [];
      for (let i = 0; i < 7; i++) {
        const d = addDays(cur, i);
        const key = format(d, 'yyyy-MM-dd');
        const dn = dayNames[getDay(d)];
        const dateStr = format(d, 'dd LLL', { locale: ru });
        days.push({
          day: dn,
          date: dateStr,
          dateObj: d,
          medications: byDate[key] && byDate[key].length > 0 ? byDate[key] : [],
        });
      }
      weeks.push(days);
      cur = addDays(cur, 7);
    }
    return weeks;
  }, [viewStart, viewEnd, receipts, selectedMedicineKeys]);

  const handlePrevMonth = () => {
    hasInitializedCurrentWeek.current = false;
    onDateChange(addMonths(anchorDate, -1));
    onSelectWeek(0);
  };

  const handleNextMonth = () => {
    hasInitializedCurrentWeek.current = false;
    onDateChange(addMonths(anchorDate, 1));
    onSelectWeek(0);
  };

  const currentWeekSchedule = weekSchedules[selectedWeekIndex] ?? [];

  return (
    <>
      <MonthPickerModal
        isOpen={isMonthPickerOpen}
        onClose={() => setIsMonthPickerOpen(false)}
        currentDate={anchorDate}
        onSelectMonth={(date) => {
          hasInitializedCurrentWeek.current = false;
          onDateChange(date);
        }}
        buttonRef={calendarButtonRef}
      />
      <MedicineFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        receipts={receipts}
        selectedMedicineKeys={selectedMedicineKeys}
        onSelectMedicines={setSelectedMedicineKeys}
      />
      <div className="relative flex size-full flex-col items-start gap-[24px] pl-0 md:py-3">
        {/* Header */}
        <div className="relative flex w-full items-center gap-[24px]">
          <div className="relative inline-flex items-center gap-4">
            <div className="relative mt-[-1.00px] w-fit whitespace-nowrap text-3xl font-medium leading-9 tracking-normal text-black">
              {monthTitle}
            </div>
          </div>
          <div className="relative flex w-full items-center gap-3 md:justify-between">
            <button
              ref={calendarButtonRef}
              type="button"
              onClick={() => setIsMonthPickerOpen(true)}
              className="relative flex size-[44px] items-center justify-center rounded-[50%] bg-[#151515] transition-colors hover:bg-[#151515]/90 md:size-auto md:rounded-none md:bg-transparent md:p-0 md:hover:bg-transparent"
            >
              <CalendarIcon className="md:text-inherit relative text-white" />
            </button>
            <button
              type="button"
              onClick={() => setIsFilterModalOpen(true)}
              className="relative flex size-[44px] items-center justify-center rounded-[50%] bg-white transition-colors hover:bg-[#f1f7fa]"
            >
              <FilterIcon className="size-[20px]" />
            </button>
          </div>
        </div>

        {/* Weeks - desktop (existing) */}
        <div className="relative hidden w-full items-start gap-3 self-stretch md:flex">
          <button
            type="button"
            onClick={handlePrevMonth}
            className="relative inline-flex flex-[0_0_auto] rotate-180 flex-col items-start justify-center self-stretch rounded-2xl bg-white p-2"
          >
            <div className="relative flex size-5 rotate-180 items-center justify-center">
              <svg
                width="6"
                height="12"
                viewBox="0 0 6 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.45215 1.28014C5.74504 0.987243 5.74504 0.512483 5.45215 0.219589C5.15924 -0.0731421 4.68444 -0.0732498 4.3916 0.219589L1.6543 2.95689C1.26706 3.34413 0.944099 3.6662 0.703125 3.95006C0.456544 4.24054 0.251713 4.54038 0.134766 4.90025C-0.0445833 5.45238 -0.0445433 6.04733 0.134766 6.59947C0.251686 6.95931 0.456591 7.25921 0.703125 7.54967C0.944069 7.83349 1.26712 8.15566 1.6543 8.54283L4.3916 11.2801C4.68449 11.573 5.15925 11.573 5.45215 11.2801C5.74504 10.9872 5.74504 10.5125 5.45215 10.2196L2.71484 7.48228C2.31019 7.07763 2.03877 6.80524 1.84668 6.57896C1.66071 6.35985 1.59298 6.23315 1.56152 6.13658C1.48001 5.88558 1.47997 5.61414 1.56152 5.36314C1.593 5.26656 1.66065 5.13992 1.84668 4.92076C2.03879 4.69446 2.31011 4.42218 2.71484 4.01744L5.45215 1.28014Z"
                  fill="#151515"
                />
              </svg>
            </div>
          </button>

          {weekMeta.weekRanges.map((week, index) => {
            const isSelected = selectedWeekIndex === index;
            return (
              <button
                key={index}
                type="button"
                onClick={() => onSelectWeek(index)}
                className={`relative flex flex-1 grow flex-col items-start justify-center overflow-hidden rounded-2xl ${
                  isSelected
                    ? 'my-[-4.00px] border-2 border-solid bg-[#151515]'
                    : 'bg-[#e4f0f7]'
                }`}
              >
                <div
                  className={`relative flex w-full flex-[0_0_auto] flex-col items-center justify-center gap-[24px] px-2 py-1.5 ${
                    isSelected ? ' bg-[#151515]' : 'bg-[#e4f0f7]'
                  }`}
                >
                  <div
                    className={`relative mt-[-1.00px] self-stretch text-center text-xs font-normal leading-4 tracking-normal ${
                      isSelected ? 'text-white' : 'text-[#151515] opacity-50'
                    }`}
                  >
                    {index + 1} неделя
                  </div>
                </div>
                <div
                  className={`relative grid w-full flex-[0_0_auto] flex-col items-start self-stretch rounded-2xl bg-white p-2 ${
                    isSelected ? '' : ' text-[#151515]'
                  }`}
                >
                  <div
                    className={` self-stretch text-center text-xl font-medium leading-7 tracking-normal text-[#151515]`}
                  >
                    {week.range}
                  </div>
                  <div className="self-stretch text-center text-sm font-normal leading-5 tracking-normal text-[#151515] opacity-50">
                    {week.subtitle}
                  </div>
                </div>
              </button>
            );
          })}

          <button
            type="button"
            onClick={handleNextMonth}
            className="relative inline-flex flex-[0_0_auto] flex-col items-start justify-center self-stretch rounded-2xl bg-white p-2"
          >
            <div className="relative flex size-5 rotate-180 items-center justify-center">
              <svg
                width="6"
                height="12"
                viewBox="0 0 6 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.45215 1.28014C5.74504 0.987243 5.74504 0.512483 5.45215 0.219589C5.15924 -0.0731421 4.68444 -0.0732498 4.3916 0.219589L1.6543 2.95689C1.26706 3.34413 0.944099 3.6662 0.703125 3.95006C0.456544 4.24054 0.251713 4.54038 0.134766 4.90025C-0.0445833 5.45238 -0.0445433 6.04733 0.134766 6.59947C0.251686 6.95931 0.456591 7.25921 0.703125 7.54967C0.944069 7.83349 1.26712 8.15566 1.6543 8.54283L4.3916 11.2801C4.68449 11.573 5.15925 11.573 5.45215 11.2801C5.74504 10.9872 5.74504 10.5125 5.45215 10.2196L2.71484 7.48228C2.31019 7.07763 2.03877 6.80524 1.84668 6.57896C1.66071 6.35985 1.59298 6.23315 1.56152 6.13658C1.48001 5.88558 1.47997 5.61414 1.56152 5.36314C1.593 5.26656 1.66065 5.13992 1.84668 4.92076C2.03879 4.69446 2.31011 4.42218 2.71484 4.01744L5.45215 1.28014Z"
                  fill="#151515"
                />
              </svg>
            </div>
          </button>
        </div>

        {/* Weeks - mobile (scrollable) */}
        <div className="w-full md:hidden">
          <div className="flex w-full gap-[12px] overflow-x-auto pb-[4px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {weekMeta.weekRanges.map((week, index) => {
              const isSelected = selectedWeekIndex === index;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => onSelectWeek(index)}
                  className={`flex min-w-[220px] flex-col overflow-hidden rounded-2xl ${
                    isSelected ? 'border-2 border-solid border-[#151515]' : ''
                  }`}
                >
                  <div
                    className={`flex w-full flex-col items-center justify-center px-2 py-1.5 ${
                      isSelected ? 'bg-[#151515]' : 'bg-[#e4f0f7]'
                    }`}
                  >
                    <div
                      className={`mt-[-1.00px] text-center text-xs font-normal leading-4 tracking-normal ${
                        isSelected ? 'text-white' : 'text-[#151515] opacity-50'
                      }`}
                    >
                      {index + 1} неделя
                    </div>
                  </div>
                  <div className="rounded-2xl bg-white p-2">
                    <div className="text-center text-xl font-medium leading-7 tracking-normal text-[#151515]">
                      {week.range}
                    </div>
                    <div className="text-center text-sm font-normal leading-5 tracking-normal text-[#151515] opacity-50">
                      {week.subtitle}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Days list */}
        <div className="relative w-full flex-1 overflow-y-auto rounded-2xl md:rounded-2xl">
          {/* Desktop (existing) */}
          <div className="hidden flex-col items-start gap-3 md:flex">
            {currentWeekSchedule.map((daySchedule, dayIndex) => {
              const isToday =
                format(daySchedule.dateObj, 'yyyy-MM-dd') ===
                format(new Date(), 'yyyy-MM-dd');
              return (
                <div
                  key={dayIndex}
                  className="relative flex w-full flex-[0_0_auto] items-start gap-3 self-stretch"
                >
                  <div
                    className={`relative flex w-[68px] items-center justify-around gap-[509px] self-stretch rounded-[20px] p-3 ${
                      isToday ? 'bg-[#151515]' : 'bg-white'
                    }`}
                  >
                    <div
                      className={`flex-1 text-center text-sm font-normal leading-5 tracking-normal ${
                        isToday
                          ? 'font-medium text-white'
                          : 'text-[#151515] opacity-50'
                      }`}
                    >
                      {daySchedule.day}
                      <br />
                      <span className="whitespace-nowrap">
                        {format(daySchedule.dateObj, 'd LLL', { locale: ru })}
                      </span>
                    </div>
                  </div>
                  {daySchedule.medications.length === 0 ? (
                    <div className="relative flex flex-1 grow flex-col items-center justify-center self-stretch rounded-[20px] bg-white/50 p-3">
                      <span className="relative mt-[-1.00px] flex items-center justify-center self-stretch text-center text-sm font-normal leading-5 tracking-normal text-[#151515] opacity-50">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7.77115 1.42834L1.42752 7.77197C0.323069 8.87642 0.323069 10.6671 1.42752 11.7715L1.42796 11.772C2.53241 12.8764 4.32308 12.8764 5.42754 11.772L11.7712 5.42836C12.8756 4.3239 12.8756 2.53323 11.7712 1.42878L11.7707 1.42834C10.6663 0.323889 8.8756 0.323889 7.77115 1.42834Z"
                            stroke="#151515"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        &nbsp; У вас нет назначений на этот день
                      </span>
                    </div>
                  ) : (
                    daySchedule.medications.map((med, medIndex) => (
                      <div
                        key={medIndex}
                        className={`relative flex flex-1 grow flex-col items-start gap-3 self-stretch rounded-[20px] p-3 ${med.bgColor}`}
                      >
                        <div className="relative flex w-full flex-[0_0_auto] flex-col items-start gap-1 self-stretch">
                          <div className="mt-[-1.00px] self-stretch text-sm font-bold leading-5 tracking-normal text-[#151515]">
                            {med.name}
                          </div>
                          <div className="relative flex w-full flex-[0_0_auto] flex-col items-start justify-center gap-0.5 self-stretch">
                            <div className="relative mt-[-1.00px] self-stretch text-xs font-normal leading-4 tracking-normal text-[#151515]">
                              {med.dosage ?? ''}
                            </div>
                            <div className="self-stretch text-xs font-normal leading-4 tracking-normal text-[#151515] opacity-50">
                              {med.time}
                            </div>
                          </div>
                        </div>
                        {med.note && (
                          <div
                            className={`relative flex w-full flex-[0_0_auto] items-center justify-center gap-3 rounded-[8px] border-l-2 py-2.5 pl-3 pr-2.5 ${
                              med.noteBgColor || 'bg-[#f7f8f9]'
                            } ${med.noteBorderColor}`}
                          >
                            <p className="relative mt-[-2.00px] flex-1 text-xs font-normal leading-4 tracking-normal text-[#151515]">
                              {med.note}
                            </p>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              );
            })}
          </div>

          {/* Mobile: fixed day + meds stacked vertically */}
          <div className="flex flex-col items-start gap-3 md:hidden">
            {currentWeekSchedule.map((daySchedule, dayIndex) => {
              const isToday =
                format(daySchedule.dateObj, 'yyyy-MM-dd') ===
                format(new Date(), 'yyyy-MM-dd');
              return (
                <div key={dayIndex} className="flex w-full items-stretch gap-3">
                  <div
                    className={`flex w-[68px] shrink-0 items-center justify-around self-stretch rounded-[20px] p-3 ${
                      isToday ? 'bg-[#151515]' : 'bg-white'
                    }`}
                  >
                    <div
                      className={`flex-1 text-center text-sm font-normal leading-5 tracking-normal ${
                        isToday
                          ? 'font-medium text-white'
                          : 'text-[#151515] opacity-50'
                      }`}
                    >
                      {daySchedule.day}
                      <br />
                      <span className="whitespace-nowrap">
                        {format(daySchedule.dateObj, 'd LLL', { locale: ru })}
                      </span>
                    </div>
                  </div>

                  {daySchedule.medications.length === 0 ? (
                    <div className="flex flex-1 items-center justify-center rounded-[20px] bg-white/50 p-3">
                      <span className="flex items-center justify-center text-center text-sm font-normal leading-5 tracking-normal text-[#151515] opacity-50">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7.77115 1.42834L1.42752 7.77197C0.323069 8.87642 0.323069 10.6671 1.42752 11.7715L1.42796 11.772C2.53241 12.8764 4.32308 12.8764 5.42754 11.772L11.7712 5.42836C12.8756 4.3239 12.8756 2.53323 11.7712 1.42878L11.7707 1.42834C10.6663 0.323889 8.8756 0.323889 7.77115 1.42834Z"
                            stroke="#151515"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        &nbsp; У вас нет назначений на этот день
                      </span>
                    </div>
                  ) : (
                    <div className="flex-1">
                      <div className="flex flex-col gap-[12px]">
                        {daySchedule.medications.map((med, medIndex) => (
                          <div
                            key={medIndex}
                            className={`flex w-full flex-col gap-3 rounded-[20px] p-3 ${med.bgColor}`}
                          >
                            <div className="flex flex-col gap-1">
                              <div className="text-sm font-bold leading-5 tracking-normal text-[#151515]">
                                {med.name}
                              </div>
                              <div className="flex flex-col gap-0.5">
                                <div className="text-xs font-normal leading-4 tracking-normal text-[#151515]">
                                  {med.dosage ?? ''}
                                </div>
                                <div className="text-xs font-normal leading-4 tracking-normal text-[#151515] opacity-50">
                                  {med.time}
                                </div>
                              </div>
                            </div>
                            {med.note && (
                              <div
                                className={`flex items-center gap-3 rounded-[8px] border-l-2 py-2.5 pl-3 pr-2.5 ${
                                  med.noteBgColor || 'bg-[#f7f8f9]'
                                } ${med.noteBorderColor}`}
                              >
                                <p className="flex-1 text-xs font-normal leading-4 tracking-normal text-[#151515]">
                                  {med.note}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};
